// ./src/utils/api/StorageStatsService.ts
//
// Account-level storage usage from ms_storage's account_stat table (files count +
// total MB). Stats are tracked per (account, user), so we sum the account's rows
// for the whole-account total shown on the Storage page. The *limit* comes from
// the plan feature `storage_mb` (see useStorageStats), not from here.
//
// NOTE: accountStatList isn't auto-scoped server-side, so we pass the caller's own
// accountId in the filter.

import { gql } from "@apollo/client";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";

const ACCOUNT_STAT_LIST_QUERY = gql`
  query AccountStatList($filter: AccountStatFilter) {
    accountStatList(filter: $filter) {
      code
      errors {
        name
        errors
      }
      data {
        accountId
        userId
        filesCount
        totalSizeMb
      }
    }
  }
`;

export interface StorageUsage {
  filesCount: number;
  usedMb: number;
}

export class StorageStatsService {
  /** Sum the account's stat rows into a single usage figure. */
  static async getUsage(accountId: string): Promise<StorageUsage> {
    const result = await ApiService.executeQuery(ACCOUNT_STAT_LIST_QUERY, {
      filter: { accountId },
    });

    if (result.code !== OP_RESULT_CODES.OK) {
      return { filesCount: 0, usedMb: 0 };
    }

    const rows: Array<{ filesCount?: number; totalSizeMb?: number }> = Array.isArray(result.data)
      ? result.data
      : [];

    return rows.reduce<StorageUsage>(
      (acc, row) => ({
        filesCount: acc.filesCount + (row.filesCount ?? 0),
        usedMb: acc.usedMb + (row.totalSizeMb ?? 0),
      }),
      { filesCount: 0, usedMb: 0 }
    );
  }
}
